/* finger-chooser/round.js — one round of «everyone puts a finger down».
 * Pure state machine with an injected clock: no DOM, no timers of its own,
 * so node tests drive it frame by frame.
 *
 * Phases
 *   idle      — nothing on the screen
 *   waiting   — fingers are down, but fewer than the mode needs
 *   counting  — enough fingers; the rings fill up for `holdMs`
 *   suspense  — the draw is MADE; the light runs over the rings to show it
 *   result    — the pick is on screen while fingers are still down
 *   shown     — every finger has left; the result stays until the next touch
 *
 * The rules people argue about, decided once:
 *   • Adding or lifting a finger while the rings fill up restarts the count —
 *     nobody joins late or slips out early.
 *   • MOVING a finger never restarts anything. The most common complaint about
 *     the finger-chooser apps is «it keeps pulsing and never picks» — shaky
 *     hands, children, a phone passed around a table. Only the SET of fingers
 *     matters, not where they are.
 *   • The draw is made when the count ends. Lifting a finger during the
 *     suspense animation does not change the result (the ring stays as a
 *     ghost), and a finger that lands during it is «late» and not in the round.
 *   • After the result a new touch starts the next round only once everyone
 *     has lifted, or after `lockMs` — so a latecomer brushing the screen
 *     cannot wipe the result before the table has seen it.
 */

import { drawRound, minParticipants, clampCount } from './draw.js';

export const PHASE = {
    IDLE: 'idle', WAIT: 'waiting', COUNT: 'counting',
    SUSPENSE: 'suspense', RESULT: 'result', SHOWN: 'shown',
};

/* Participants in the order the light runs over them: around the centroid,
 * clockwise on screen, starting at 12 o'clock. Returns ids. */
export function circleOrder(points) {
    if (!points.length) return [];
    let cx = 0, cy = 0;
    for (const p of points) { cx += p.x; cy += p.y; }
    cx /= points.length; cy /= points.length;
    const withA = points.map((p) => {
        // atan2 with y down: angle measured clockwise from 12 o'clock.
        let a = Math.atan2(p.x - cx, -(p.y - cy));
        if (a < 0) a += Math.PI * 2;
        return { id: p.id, a, slot: p.slot || 0 };
    });
    withA.sort((u, v) => (u.a - v.a) || (u.slot - v.slot));
    return withA.map((q) => q.id);
}

/**
 * The «roulette light» of the suspense phase: hop k lights ring `pos` at
 * time `at` (ms from the start). The light passes every ring at least once,
 * slows down geometrically (last step ≈ 4× the first) and lands on
 * `target` at `landAt` = 85 % of the duration; the remaining time the
 * chosen ring blinks.
 */
export function hopSchedule(n, target, durationMs, opts) {
    opts = opts || {};
    n = Math.max(1, Math.floor(n));
    target = ((Math.floor(target) % n) + n) % n;
    const landAt = Math.max(0, Math.round(durationMs * 0.85));
    if (n === 1 || durationMs <= 0) return { hops: [{ at: 0, pos: target }], landAt: 0 };
    // Enough hops to go round at least once (twice for small groups), but
    // not so many that the steps blur together on a short suspense.
    const minHops = n <= 3 ? 2 * n + 1 : n + 1;
    const want = Math.round(landAt / 95);
    const H = Math.max(minHops, Math.min(opts.maxHops || 40, want));
    const start = ((target - (H - 1)) % n + n) % n;
    const ratio = Math.pow(4, 1 / Math.max(1, H - 2));
    let sum = 0;
    for (let j = 0; j < H - 1; j++) sum += Math.pow(ratio, j);
    const a = landAt / sum;
    const hops = [];
    let t = 0;
    for (let j = 0; j < H; j++) {
        hops.push({ at: Math.round(t), pos: (start + j) % n });
        if (j < H - 1) t += a * Math.pow(ratio, j);
    }
    hops[H - 1].at = landAt;
    return { hops, landAt };
}

/* Index of the hop that is lit at time t. */
export function hopAt(schedule, t) {
    const h = schedule.hops;
    let k = 0;
    for (let i = 0; i < h.length; i++) { if (h[i].at <= t) k = i; else break; }
    return k;
}

export function createRound(opts) {
    opts = opts || {};
    const cfg = {
        holdMs: opts.holdMs != null ? opts.holdMs : 2000,
        suspenseMs: opts.suspenseMs != null ? opts.suspenseMs : 1800,
        lockMs: opts.lockMs != null ? opts.lockMs : 1200,
        mode: opts.mode || 'winner',
        count: opts.count != null ? opts.count : 1,
    };
    cfg.count = clampCount(cfg.mode, cfg.count) || cfg.count;
    const draw = opts.draw || ((mode, n, count) => drawRound(mode, n, count, opts.random));
    const emit = opts.onEvent || function () {};

    /* id → { id, x, y, slot, state: 'down' | 'lifted' | 'late' } */
    const fingers = new Map();
    let phase = PHASE.IDLE;
    let countStart = 0;
    let suspStart = 0;
    let resultAt = 0;
    let result = null;
    let round = 0;

    function need() { return minParticipants(cfg.mode, cfg.count); }
    function downIn() {
        const a = [];
        for (const f of fingers.values()) if (f.state === 'down') a.push(f);
        return a;
    }
    function anyPhysicallyDown() {
        for (const f of fingers.values()) if (f.state === 'down' || f.state === 'late') return true;
        return false;
    }
    function freeSlot() {
        const used = new Set();
        for (const f of fingers.values()) if (f.state !== 'late') used.add(f.slot);
        let s = 0;
        while (used.has(s)) s++;
        return s;
    }

    function reevaluate(now, why) {
        const n = downIn().length;
        const prev = phase;
        if (n === 0) phase = PHASE.IDLE;
        else if (n < need()) phase = PHASE.WAIT;
        else {
            phase = PHASE.COUNT;
            countStart = now;
            emit(prev === PHASE.COUNT ? 'count-restart' : 'count-start', { n, why });
            return;
        }
        if (prev === PHASE.COUNT) emit('count-stop', { n, why });
    }

    /* Leave the result behind and make the fingers still down the players of
     * a fresh round: lifted ghosts go, late fingers join. */
    function clearResult() {
        for (const [id, f] of fingers) {
            if (f.state === 'lifted') fingers.delete(id);
            else if (f.state === 'late') f.state = 'down';
        }
        result = null;
        phase = PHASE.IDLE;
        // Late fingers had no slot of their own; give them one now.
        const taken = new Set();
        for (const f of fingers.values()) {
            if (f.slot == null || taken.has(f.slot)) f.slot = null;
            else taken.add(f.slot);
        }
        for (const f of fingers.values()) {
            if (f.slot == null) {
                let s = 0;
                while (taken.has(s)) s++;
                f.slot = s; taken.add(s);
            }
        }
    }

    function down(id, x, y, now) {
        if (fingers.has(id)) { move(id, x, y); return; }
        if (phase === PHASE.SHOWN) {
            fingers.clear();
            result = null;
            phase = PHASE.IDLE;
        } else if (phase === PHASE.RESULT) {
            if (now - resultAt >= cfg.lockMs) {
                clearResult();
            } else {
                fingers.set(id, { id, x, y, slot: null, state: 'late' });
                emit('late', { id });
                return;
            }
        } else if (phase === PHASE.SUSPENSE) {
            fingers.set(id, { id, x, y, slot: null, state: 'late' });
            emit('late', { id });
            return;
        }
        fingers.set(id, { id, x, y, slot: freeSlot(), state: 'down' });
        emit('finger', { id, n: downIn().length });
        reevaluate(now, 'add');
    }

    function move(id, x, y) {
        const f = fingers.get(id);
        if (!f || f.state === 'lifted') return;
        f.x = x; f.y = y;
    }

    function up(id, now) {
        const f = fingers.get(id);
        if (!f || f.state === 'lifted') return;
        if (f.state === 'late') {
            fingers.delete(id);
        } else if (phase === PHASE.IDLE || phase === PHASE.WAIT || phase === PHASE.COUNT) {
            fingers.delete(id);
            reevaluate(now, 'lift');
            return;
        } else {
            f.state = 'lifted';
        }
        if (phase === PHASE.RESULT && !anyPhysicallyDown()) {
            phase = PHASE.SHOWN;
            emit('cleared', {});
        }
    }

    /* The system dropped every touch (touchcancel for all — iOS does this when
     * more fingers land than it tracks, some Androids for a three-finger
     * gesture). During the count that ends the round: the fingers are still
     * physically there, but the page will never hear about them again until
     * they are lifted and put back. */
    function cancelAll(now, reason) {
        if (phase === PHASE.IDLE || phase === PHASE.WAIT || phase === PHASE.COUNT) {
            const n = downIn().length;
            fingers.clear();
            const prev = phase;
            phase = PHASE.IDLE;
            if (n > 0) emit('cancel', { n, reason: reason || 'cancel', during: prev });
            return;
        }
        for (const id of Array.from(fingers.keys())) up(id, now);
    }

    /* Reconcile with the authoritative list of touches the browser still has
     * (TouchEvent.targetTouches). A lost touchend otherwise leaves a ghost ring
     * that holds the round forever. */
    function sync(ids, now) {
        const keep = ids instanceof Set ? ids : new Set(ids);
        for (const [id, f] of Array.from(fingers)) {
            if (f.state !== 'lifted' && !keep.has(id)) up(id, now);
        }
    }

    function setConfig(c, now) {
        let changed = false;
        if (c.mode && c.mode !== cfg.mode) { cfg.mode = c.mode; changed = true; }
        if (c.count != null) {
            const cc = clampCount(cfg.mode, c.count);
            if (cc !== cfg.count) { cfg.count = cc; changed = true; }
        } else if (changed) {
            cfg.count = clampCount(cfg.mode, cfg.count);
        }
        if (c.holdMs != null) cfg.holdMs = c.holdMs;
        if (c.suspenseMs != null) cfg.suspenseMs = c.suspenseMs;
        if (!changed) return;
        if (phase === PHASE.SUSPENSE) return; // the UI locks the controls; the draw stands
        if (phase === PHASE.RESULT || phase === PHASE.SHOWN) clearResult();
        reevaluate(now, 'config');
    }

    function tick(now) {
        if (phase === PHASE.COUNT && now - countStart >= cfg.holdMs) {
            const players = downIn().sort((a, b) => a.slot - b.slot);
            const n = players.length;
            const res = draw(cfg.mode, n, cfg.count);
            const ids = players.map((f) => f.id);
            const ring = circleOrder(players);
            round++;
            result = {
                round,
                mode: res.mode,
                count: res.count,
                ids,
                ring,
                chosen: new Set((res.chosen || []).map((i) => ids[i])),
                chosenOrder: (res.chosen || []).map((i) => ids[i]),
                teamOf: new Map(),
                placeOf: new Map(),
                at: { },
                hops: null,
            };
            if (res.teamOf) res.teamOf.forEach((t, i) => result.teamOf.set(ids[i], t));
            if (res.placeOf) res.placeOf.forEach((p, i) => result.placeOf.set(ids[i], p));
            for (const f of players) result.at[f.id] = { x: f.x, y: f.y, slot: f.slot };
            if (result.chosenOrder.length) {
                result.hops = hopSchedule(ring.length, ring.indexOf(result.chosenOrder[0]), cfg.suspenseMs);
            }
            phase = PHASE.SUSPENSE;
            suspStart = now;
            emit('draw', result);
        }
        if (phase === PHASE.SUSPENSE && now - suspStart >= cfg.suspenseMs) {
            phase = PHASE.RESULT;
            resultAt = now;
            emit('result', result);
            if (!anyPhysicallyDown()) {
                phase = PHASE.SHOWN;
                emit('cleared', {});
            }
        }
    }

    function view(now) {
        const list = [];
        for (const f of fingers.values()) list.push({ id: f.id, x: f.x, y: f.y, slot: f.slot, state: f.state });
        return {
            phase,
            fingers: list,
            n: downIn().length,
            need: need(),
            mode: cfg.mode,
            count: cfg.count,
            holdMs: cfg.holdMs,
            suspenseMs: cfg.suspenseMs,
            progress: phase === PHASE.COUNT ? Math.max(0, Math.min(1, (now - countStart) / cfg.holdMs)) : 0,
            suspenseT: phase === PHASE.SUSPENSE ? now - suspStart : null,
            sinceResult: (phase === PHASE.RESULT || phase === PHASE.SHOWN) ? now - resultAt : null,
            result,
        };
    }

    /* Forget everything (tab switch, mode reset). */
    function reset() {
        fingers.clear();
        result = null;
        phase = PHASE.IDLE;
    }

    return {
        down, move, up, cancelAll, sync, setConfig, tick, view, reset,
        get phase() { return phase; },
        get config() { return Object.assign({}, cfg); },
        get busy() { return phase === PHASE.COUNT || phase === PHASE.SUSPENSE || (phase === PHASE.RESULT && anyPhysicallyDown()) || phase === PHASE.WAIT; },
    };
}
